import { FC, useState } from 'react';
import {
  Grid,
  Box,
  TextField,
  Switch,
  FormControlLabel,
  Paper,
} from '@mui/material';
import { FieldErrors, useForm, Controller, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import FormButtons from '../../../../app/components/FormButtons';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { useAppDispatch } from '../../../../app/store/configureStore';
import {
  createMealItemCustomizationOptionAsync,
  updateMealItemCustomizationOptionAsync,
} from './updateMealItemCustomizationSlice';
import { MealItemCustomizationOptionFormData } from '../../../../app/data/models/mealItems/mealItemCustomizationFormData';
import { SubmitHandler } from 'react-hook-form';
import {
  LanguageCode,
  LocalizedLanguageFormData,
} from '../../../../app/data/models/common/localizedLanguage';
import { mealItemCustomizationOptionValidationSchema } from './mealItemCustomizationValidation';
import { useRestaurant } from '../../../../app/hooks/useRestaurant';
import ListTitleBox from '../../../../app/components/list/ListTitleBox';
import LoadingComponent from '../../../../app/layout/LoadingComponent';
import ErrorComponent from '../../../../app/layout/ErrorComponent';
import { LanguageFormTabs } from '../../../../app/components/LanguageFormTabs';

interface Props {
  customizationId: string;
  option?: MealItemCustomizationOptionFormData;
  restaurantId: string;
  onClose: (success: boolean) => void;
}

const MealItemCustomizationOptionForm: FC<Props> = ({
  customizationId,
  option,
  restaurantId,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    restaurant,
    isLoading: isRestaurantLoading,
    error: restaurantError,
    retry: retryRestaurant,
  } = useRestaurant(restaurantId);
  const languageCodes = restaurant?.languageCodes || [];
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<number>(0);

  const { control, handleSubmit } =
    useForm<MealItemCustomizationOptionFormData>({
      mode: 'onTouched',
      resolver: yupResolver(
        mealItemCustomizationOptionValidationSchema(
          t,
          (restaurant?.languageCodes || []) as LanguageCode[]
        )
      ) as unknown as Resolver<MealItemCustomizationOptionFormData>,
      defaultValues: {
        names: (restaurant?.languageCodes || []).reduce(
          (acc, code) => ({
            ...acc,
            [code]: option?.names[code] || '',
          }),
          {} as LocalizedLanguageFormData
        ),
        price: option?.price || 0,
        isAvailable:
          option?.isAvailable !== undefined ? option.isAvailable : true,
      },
    });

  const onSubmit: SubmitHandler<MealItemCustomizationOptionFormData> = async (
    data
  ) => {
    setIsSubmitting(true);
    try {
      if (option?.id) {
        await dispatch(
          updateMealItemCustomizationOptionAsync({
            restaurantId: restaurantId,
            customizationId,
            optionId: option.id,
            data,
          })
        ).unwrap();
      } else {
        await dispatch(
          createMealItemCustomizationOptionAsync({
            restaurantId: restaurantId,
            customizationId,
            data,
          })
        ).unwrap();
      }
      toast.success(
        option
          ? t('customization-option.update.success')
          : t('customization-option.create.success')
      );
      onClose(true);
    } catch (_error: unknown) {
      if (_error instanceof AxiosError) {
        toast.error(_error.response?.data?.title || t('common.error'));
      } else if (_error instanceof Error) {
        toast.error(_error.message || t('common.error'));
      } else {
        toast.error(t('common.error'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onError = (
    errors: FieldErrors<MealItemCustomizationOptionFormData>
  ) => {
    if (!languageCodes.length) return;

    const nameErrors = errors.names;
    if (nameErrors) {
      const errorLanguageIndex = languageCodes.findIndex(
        (code) => nameErrors[code]
      );

      if (errorLanguageIndex >= 0) {
        setActiveTab(errorLanguageIndex);
      }
    }
  };

  const header = (
    <ListTitleBox
      title={
        option
          ? t('customization-option.form.edit.title')
          : t('customization-option.form.create.title')
      }
      onBackClick={() => onClose(false)}
    />
  );

  if (isRestaurantLoading) return <LoadingComponent header={header} />;
  if (restaurantError || !restaurant)
    return <ErrorComponent onPrimaryAction={retryRestaurant} header={header} />;

  return (
    <>
      {header}
      <Box component={Paper} sx={{ width: '100%', mb: 3, p: 2 }}>
        <form onSubmit={handleSubmit(onSubmit, onError)}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <LanguageFormTabs
                languageCodes={restaurant.languageCodes}
                control={control}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                fields={[
                  {
                    name: 'names',
                    label: t('customization-option.form.fields.name'),
                    multiline: true,
                    rows: 3,
                  },
                ]}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="price"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type="number"
                    label={t('customization-option.form.fields.price')}
                    error={!!error}
                    helperText={error?.message}
                    fullWidth
                    variant="outlined"
                    inputProps={{ step: '0.01' }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="isAvailable"
                control={control}
                defaultValue={true}
                render={({ field: { value, onChange } }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={value}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                    }
                    label={t('customization-option.form.fields.available')}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <FormButtons
              loading={isSubmitting}
              submitText={option ? t('common.update') : t('common.create')}
            />
          </Box>
        </form>
      </Box>
    </>
  );
};

export default MealItemCustomizationOptionForm;
